import { z } from 'zod';

import { StakeholderType } from '../../../types/stakeholder.types';
import { getTranslation } from '../../../utils/get-translation.util';

export const MAX_FILE_SIZE = 1024 * 1024 * 10;

export const ACCEPTED_FILE_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'image/png',
  'image/jpeg',
];

export const stakeholderTypeDefaultPermissionRestriction: {
  default: { capTable: boolean; companyTable: boolean; equity: boolean; position: boolean };
} & PartialRecord<
  StakeholderType,
  { capTable: boolean; companyTable: boolean; equity: boolean; position: boolean }
> = {
  [StakeholderType.FOUNDER]: {
    capTable: true,
    position: true,
    equity: true,
    companyTable: true,
  },
  [StakeholderType.EMPLOYEE]: {
    capTable: false,
    position: false,
    equity: false,
    companyTable: false,
  },
  [StakeholderType.ADVISOR]: {
    capTable: false,
    position: false,
    equity: false,
    companyTable: false,
  },
  default: {
    capTable: false,
    position: false,
    equity: true,
    companyTable: true,
  },
};

export const fileSchemaMessage = getTranslation('FILE_SIZE_TOO_LARGE');

const phoneRegex = new RegExp(/^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/);

const humanSchema = z
  .object({
    nationality: z
      .object({
        name: z.string().min(1, { message: getTranslation('REQUIRED') }),
        iso2Code: z.string(),
      })
      .optional(),
    passportNo: z.string().or(z.literal('')).default(''),
    passportExpiryDate: z
      .object({
        day: z.coerce.number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') }),
        month: z.coerce.number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') }),
        year: z.coerce.number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') }),
      })
      .optional(),
    birthdayDate: z
      .object({
        day: z.coerce.number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') }),
        month: z.coerce.number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') }),
        year: z.coerce.number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') }),
      })
      .optional(),
  })
  .optional();

const companySchema = z
  .object({
    incorporationCountry: z
      .object({
        name: z.string().min(1, { message: getTranslation('REQUIRED') }),
        iso2Code: z.string(),
      })
      .optional(),
    identifier: z.string().or(z.literal('')).default(''),
    incorporationDate: z
      .object({
        day: z.coerce.number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') }),
        month: z.coerce.number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') }),
        year: z.coerce.number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') }),
      })
      .optional(),
  })
  .optional();

export const stepOne = z
  .object({
    fullName: z
      .string()
      .trim()
      .min(1, 'Min length 1 character')
      .max(100, 'Max length 100 characters'),

    email: z.email(),

    subEmails: z
      .array(
        z.object({
          value: z.email(),
        }),
      )
      .max(10),

    stakeholderType: z.enum(StakeholderType),

    human: humanSchema.optional(),

    company: companySchema.optional(),

    access: z
      .object({
        capTable: z.boolean().optional().default(true),
        position: z.boolean().optional().default(true),
        equity: z.boolean().optional().default(true),
        companyTable: z.boolean().optional().default(true),
      })
      .optional(),

    phoneCountry: z
      .object({
        iso2Code: z.string(),
        name: z.string(),
        prefix: z.string(),
      })
      .optional(),
    phoneNumber: z
      .string()
      .max(15, getTranslation('INVALID_NUMBER'))
      .regex(phoneRegex, getTranslation('INVALID_NUMBER'))
      .or(z.literal(''))
      .default(''),
    address: z.string().trim().min(1).or(z.literal('')).default(''),
  })
  .superRefine((data, ctx) => {
    const seen = new Set<string>();

    const mainEmail = data.email.toLowerCase();
    seen.add(mainEmail);

    data.subEmails?.forEach((item, index) => {
      const value = item.value.toLowerCase();

      if (seen.has(value)) {
        ctx.addIssue({
          code: 'custom',
          message:
            value === mainEmail
              ? 'Sub email must be different from main email'
              : 'Sub emails must be unique',
          path: ['subEmails', index, 'value'],
        });
      } else {
        seen.add(value);
      }
    });
  });

export const stepTwo = z
  .object({
    additionalNotes: z
      .string()
      .max(3000, getTranslation('MAXIMUM_CHARACTERS', { count: 3000 }))
      .optional(),
    files: z
      .object({
        docLink: z.string(),
        loadProgress: z.number().max(100),
        abort: z.function(),
        id: z.string(),
        doc: z
          .custom<File>()
          .or(
            z.object({
              size: z.number(),
              type: z.string(),
              name: z.string(),
            }),
          )
          .refine(
            ({ type }) => ACCEPTED_FILE_MIME_TYPES.includes(type),
            getTranslation('FILE_FORMAT_NOT_SUPPORTED'),
          )
          .refine(({ size }) => size <= MAX_FILE_SIZE, fileSchemaMessage),
      })
      .array()

      .optional(),
  })
  .optional();

export const formSchema = z.object({
  stepOne,
  stepTwo,
});

export type FormSchema = z.infer<typeof formSchema>;
